"use client";

import { useEffect, useState } from 'react';
import Link from 'next/link';
import { Menu, X } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { InstallPwaButton } from '@/components/pwa/InstallPwaButton';
import { Button } from '@/components/ui/button';
import { PublicLanguageSwitcher } from './language-switcher';
import { ScopaxLogo } from "@/components/ui/scopax-logo";
import { isFoundingPartnerPhase } from '@/lib/feature-flags/launch-phase';

export function MarketingHeader() {
  const t = useTranslations('marketing.navigation');
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [sessionStatus, setSessionStatus] = useState<'unknown' | 'anon' | 'authed'>('unknown');
  const [dashboardHref, setDashboardHref] = useState<string | null>(null);

  useEffect(() => {
    if (!mobileNavOpen) {
      return;
    }
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setMobileNavOpen(false);
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [mobileNavOpen]);

  useEffect(() => {
    let active = true;

    const resolveDashboardSlug = async () => {
      try {
        if (!active) return;

        const response = await fetch('/api/companies/current', { credentials: 'include' });
        if (!active) return;

        if (!response.ok) {
          setSessionStatus('anon');
          return;
        }

        const payload = (await response.json()) as { data?: { companySlug?: string; company_slug?: string } };
        const slug = payload?.data?.companySlug ?? payload?.data?.company_slug;
        setSessionStatus('authed');
        setDashboardHref(slug ? `/protected/${slug}` : '/protected');
      } catch {
        if (active) setSessionStatus('anon');
      }
    };

    void resolveDashboardSlug();
    return () => {
      active = false;
    };
  }, []);

  const closeMobileNav = () => setMobileNavOpen(false);

  const founding = isFoundingPartnerPhase();

  const navigationLinks = (
    <>
      <Link
        href="/"
        className="text-sm font-medium transition-colors hover:text-primary"
        onClick={closeMobileNav}
      >
        {t('home')}
      </Link>
      {!founding && (
        <Link
          href="/pricing"
          className="text-sm font-medium transition-colors hover:text-primary"
          onClick={closeMobileNav}
        >
          {t('pricing')}
        </Link>
      )}
      <Link
        href="/docs"
        className="text-sm font-medium transition-colors hover:text-primary"
        onClick={closeMobileNav}
      >
        {t('docs', { defaultValue: 'Docs' })}
      </Link>
      <Link
        href="/blog"
        className="text-sm font-medium transition-colors hover:text-primary"
        onClick={closeMobileNav}
      >
        {t('blog', { defaultValue: 'Blog' })}
      </Link>
      <Link
        href="/contact"
        className="text-sm font-medium transition-colors hover:text-primary"
        onClick={closeMobileNav}
      >
        {t('contact')}
      </Link>
    </>
  );

  const authButtons =
    sessionStatus === 'authed' ? (
      <Link href={dashboardHref ?? '/protected'} onClick={closeMobileNav}>
        <Button size="sm">
          {t('dashboard', { defaultValue: 'Go to dashboard' })}
        </Button>
      </Link>
    ) : (
      <>
        <Link href="/auth/login" onClick={closeMobileNav}>
          <Button variant="ghost" size="sm">
            {t('signIn')}
          </Button>
        </Link>
        <Link href="/auth/sign-up" onClick={closeMobileNav}>
          <Button size="sm">
            {t('signUp')}
          </Button>
        </Link>
      </>
    );

  return (
    <header className="sticky top-0 z-50 w-full border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="container mx-auto h-20 px-4 sm:h-24">
        <div className="flex h-full items-center justify-between">
          <Link
            href="/"
            className="flex items-center space-x-3 transition-opacity duration-200 hover:opacity-80"
            aria-label="Scopax - Return to homepage"
            onClick={closeMobileNav}
          >
            <ScopaxLogo className="h-8 w-8" />
            <span className="text-2xl font-semibold tracking-tight sm:text-3xl">
              SCOPAX
            </span>
          </Link>

          <nav className="hidden items-center space-x-6 md:flex" aria-label={t('menuLabel')}>
            {navigationLinks}
          </nav>

          <div className="hidden items-center space-x-4 md:flex">
            {process.env.NEXT_PUBLIC_ENABLE_PUBLIC_LANGUAGE_SWITCHER === 'true' && (
              <PublicLanguageSwitcher />
            )}
            {authButtons}
          </div>

          <div className="flex items-center gap-2 md:hidden">
            {process.env.NEXT_PUBLIC_ENABLE_PUBLIC_LANGUAGE_SWITCHER === 'true' && (
              <PublicLanguageSwitcher />
            )}
            <Button
              variant="ghost"
              size="icon"
              aria-label={mobileNavOpen ? t('closeMenu') : t('openMenu')}
              aria-expanded={mobileNavOpen}
              onClick={() => setMobileNavOpen((open) => !open)}
            >
              {mobileNavOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
          </div>
        </div>
      </div>

      {mobileNavOpen && (
        <div className="border-t border-border bg-background">
          <div className="container mx-auto px-4 py-6" aria-label={t('menuLabel')}>
            <div className="flex flex-col space-y-4">{navigationLinks}</div>
            <div className="mt-6 flex flex-col space-y-3">
              <InstallPwaButton className="w-full" />
              {authButtons}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
